import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import PWAInstallBanner from '@/components/PWAInstallBanner';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AdminDashboard from '@/pages/AdminDashboard';
import RoleBasedDashboard from '@/pages/RoleBasedDashboard';

// Lazy load pages for better performance
const Calculator = lazy(() => import('@/pages/Calculator'));
const BrandManagement = lazy(() => import('@/pages/BrandManagement'));
const BranchManagement = lazy(() => import('@/pages/BranchManagement'));
const ModelManagement = lazy(() => import('@/pages/ModelManagementNew'));
const PriceListManagement = lazy(() => import('@/pages/PriceListManagement'));
const Settings = lazy(() => import('@/pages/Settings'));
const UserManagement = lazy(() => import('@/pages/UserManagement'));
const RoleTemplates = lazy(() => import('@/pages/RoleTemplates'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const Reports = lazy(() => import('@/pages/Reports'));
const DecisionSupport = lazy(() => import('@/pages/DecisionSupport'));
const BonusManagement = lazy(() => import('@/pages/BonusManagement'));
const SalesDataEntry = lazy(() => import('@/pages/SalesDataEntry'));
const PerformanceReport = lazy(() => import('@/pages/PerformanceReport'));
const ConsultantDetail = lazy(() => import('@/pages/ConsultantDetail'));
const InsuranceManagement = lazy(() => import('@/pages/InsuranceManagement'));
const RefundManagement = lazy(() => import('@/pages/RefundManagement'));
const PreOrderManagement = lazy(() => import('@/pages/PreOrderManagement'));
const TaskManagement = lazy(() => import('@/pages/TaskManagement'));
const ExecutiveDashboard = lazy(() => import('@/pages/ExecutiveDashboard'));
const FactoryPriceSet = lazy(() => import('@/pages/FactoryPriceSet'));
const PurchaseManagement = lazy(() => import('@/pages/PurchaseManagement'));
const CRMManagement = lazy(() => import('@/pages/CRMManagement'));
const ManagerReports = lazy(() => import('@/pages/ManagerReports'));
const AppointmentsPage = lazy(() => import('@/pages/AppointmentsPage'));
const CampaignManagement = lazy(() => import('@/pages/CampaignManagement'));
const ProcurementDecision = lazy(() => import('@/pages/ProcurementDecision'));
const UserProfile = lazy(() => import('@/pages/UserProfile'));
const AuditLogs = lazy(() => import('@/pages/AuditLogs'));
const MLForecasting = lazy(() => import('@/pages/MLForecasting'));
const PerformanceAnalysis = lazy(() => import('@/pages/PerformanceAnalysis'));
const DemoAccessCenter = lazy(() => import('@/pages/DemoAccessCenter'));
const PublicPreOrder = lazy(() => import('@/pages/PublicPreOrder'));
const PublicNPSSurvey = lazy(() => import('@/pages/PublicNPSSurvey'));

import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import '@/App.css';

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();
  
  // Super admin için detaylı admin dashboard
  if (user?.role === 'super_admin') {
    return <AdminDashboard />;
  }
  
  // Diğer tüm kullanıcılar için yetki bazlı dashboard
  return <RoleBasedDashboard />;
};

function App() {
  return (
    <div className="notranslate" translate="no">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
            {/* Public routes (auth gerektirmez) */}
            <Route path="/on-siparis/:campaignId" element={<PublicPreOrder />} />
            <Route path="/nps-survey/:token" element={<PublicNPSSurvey />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/brands"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'sales_support']}>
                  <BrandManagement />
                </ProtectedRoute>
              }
            />
          <Route
            path="/models"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'sales_support']}>
                <ModelManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/branches"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <BranchManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/price-lists"
            element={
              <ProtectedRoute>
                <PriceListManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calculator"
            element={
              <ProtectedRoute>
                <Calculator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/demo-access"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DemoAccessCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/role-templates"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <RoleTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="/executive"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="/decision-support"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'sales_support']}>
                <DecisionSupport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bonus"
            element={
              <ProtectedRoute>
                <BonusManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-entry"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'sales_support']}>
                <SalesDataEntry />
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance"
            element={
              <ProtectedRoute>
                <PerformanceReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultant/:repId"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'sales_support']}>
                <ConsultantDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insurance"
            element={
              <ProtectedRoute>
                <InsuranceManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refunds"
            element={
              <ProtectedRoute>
                <RefundManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/preorders"
            element={
              <ProtectedRoute>
                <PreOrderManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <TaskManagement />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/executive" 
            element={<Navigate to="/dashboard" replace />}
          />
          <Route 
            path="/factory-prices" 
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'manager', 'sales_manager']}>
                <FactoryPriceSet />
              </ProtectedRoute>
            } 
          />
          <Route path="/purchases" element={<ProtectedRoute><PurchaseManagement /></ProtectedRoute>} />
          <Route path="/crm" element={<ProtectedRoute><CRMManagement /></ProtectedRoute>} />
          <Route path="/manager-reports" element={<ProtectedRoute allowedRoles={['super_admin', 'manager', 'sales_manager', 'service_manager', 'sales_support']}><ManagerReports /></ProtectedRoute>} />
          <Route path="/appointments" element={<ProtectedRoute allowedRoles={['super_admin', 'manager', 'customer_relations', 'customer_relations_manager']}><AppointmentsPage /></ProtectedRoute>} />
          <Route path="/campaigns" element={<ProtectedRoute allowedRoles={['super_admin', 'digital_marketing']}><CampaignManagement /></ProtectedRoute>} />
          <Route path="/procurement-decision" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><ProcurementDecision /></ProtectedRoute>} />
          <Route path="/audit-logs" element={<ProtectedRoute allowedRoles={['super_admin']}><AuditLogs /></ProtectedRoute>} />
          <Route path="/ml-forecasting" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><MLForecasting /></ProtectedRoute>} />
          <Route path="/performance-analysis" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'manager']}><PerformanceAnalysis /></ProtectedRoute>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
            <Toaster position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
